import { isNotDefined } from '@sgme/fp';
import type { ReactElement } from 'react';
import { useIntl } from 'react-intl';

import { SgTooltip } from '@/components/sg-design-system/components/feedback/SgTooltip';
import type { TcValidation } from '@/store/types.ts';
import type { IntlMessageId } from '@/types/intl';
import type { TradeCaptureResponseFieldError } from '@/types/trade-capture.ts';

type Props = {
  children: ReactElement;
  errors?: TradeCaptureResponseFieldError[];
  warnings?: TradeCaptureResponseFieldError[];
  validation?: TcValidation[];
};

/**
 * Displays error/warning tooltips around input elements for trade capture validation.
 * Errors take priority over warnings when both are present.
 */
export const TradeCaptureValidationWrapper = ({ errors = [], warnings = [], validation, children }: Props) => {
  const { formatMessage } = useIntl();

  const { errors: validationErrors, warnings: validationWarnings } = splitValidationData(validation, errors, warnings);

  const formatMessages = (items: Array<{ id: number; description: string }>, messageType: 'error' | 'warning') => {
    return items
      .map((item) =>
        formatMessage({
          id: `tradecapture.${messageType}.${item.id}` as IntlMessageId,
          defaultMessage: item.description ?? '(no description)',
        }),
      )
      .join(', ');
  };

  const renderWithTooltip = (contents: string, color: 'danger' | 'warning') => (
    <SgTooltip mode="hover" defaultOpen placement="right" backgroundColor={color} color="white">
      <SgTooltip.Trigger className={`overflow-hidden invalid-${color}`}>{children}</SgTooltip.Trigger>
      <SgTooltip.Content className="p-2">{contents}</SgTooltip.Content>
    </SgTooltip>
  );

  // Show errors first (highest priority)
  if (validationErrors.length > 0) {
    const contents = formatMessages(validationErrors, 'error');
    return renderWithTooltip(contents, 'danger');
  }

  // Show warnings if no errors
  if (validationWarnings?.length > 0) {
    const contents = formatMessages(validationWarnings, 'warning');
    return renderWithTooltip(contents, 'warning');
  }

  // No errors or warnings - return children as-is
  return children;
};

function splitValidationData(
  validation: TcValidation[] | undefined,
  errors: TradeCaptureResponseFieldError[],
  warnings: TradeCaptureResponseFieldError[],
) {
  if (isNotDefined(validation)) {
    return { errors, warnings };
  }

  const validationErrors = validation.filter((v) => v.type === 'error');
  const validationWarnings = validation.filter((v) => v.type === 'warning');

  return { errors: validationErrors, warnings: validationWarnings };
}
